import {boosterImage} from '@/app/shared/utils/booster-image';
import {Component, computed, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {BoosterService} from '@services/card-game/booster.service';
import {AuthService} from '@services/auth.service';
import {BoosterCanvasComponent} from '@/app/pages/tcg/booster-canvas/booster-canvas';
import {BoosterOpeningComponent} from '@/app/pages/tcg/booster-opening/booster-opening';
import {User} from '@/app/core/models/user';
import {Inventory} from '@/app/core/models/inventory.model';
import {InventoryService} from '@services/user/inventory.service';
import {BoosterStateService} from '@services/card-game/booster-state.service';
import {ToastService} from '@services/toast.service';
import {TeamPepsButtonComponent} from '@/app/shared/components/buttons/button-complex/button-complex';
import {Booster} from '@/app/core/models/collection/booster';

@Component({
	selector: 'app-shop',
	imports: [TranslatePipe, BoosterCanvasComponent, BoosterOpeningComponent, TeamPepsButtonComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './shop.component.html',
})
export class ShopComponent implements OnInit {

	private readonly boosterService = inject(BoosterService);
	private readonly inventoryService = inject(InventoryService);
	private readonly boosterStateService = inject(BoosterStateService);
	private readonly toastService = inject(ToastService);
	protected readonly authService = inject(AuthService);
	private readonly titleService = inject(Title);
	private readonly translate = inject(TranslateService);

	/** Doit rester aligné avec MAX_BOOSTER_PURCHASE_QUANTITY côté back. */
	readonly maxQuantity = 10;

	user: WritableSignal<User | null> = signal(null);
	selectedBooster: WritableSignal<Booster | null> = signal(null);
	isPurchasing = signal(false);
	quantity = signal(1);
	boosters = signal<Booster[]>([]);
	boosterImage = boosterImage;

	/** Boosters restant à ouvrir après un achat multiple (mêmes données que l'inventaire du profil). */
	inventory = signal<Inventory | null>(null);
	isOpeningNext = signal(false);
	/** Dernier type de booster ouvert : « ouvrir le suivant » reprend ce type tant qu'il en reste. */
	private lastOpenedBoosterId: string | null = null;

	/** true tant qu'il reste au moins un booster non ouvert → l'écran d'ouverture propose « ouvrir le suivant ». */
	hasBoostersToOpen = computed(() => (this.inventory()?.boosters ?? []).some(b => b.quantity > 0));

	/** Coût total pour la quantité choisie. */
	totalPrice = computed(() => this.selectedBooster()!.price * this.quantity() || 0);

	/** Quantité max achetable : bornée par le solde et par maxQuantity. */
	maxAffordable = computed(() => {
		const coins = this.user()?.coins;
		if (coins === undefined) return this.maxQuantity;
		return Math.max(1, Math.min(this.maxQuantity, Math.floor(coins / this.selectedBooster()!.price || 1)));
	});

	canAfford = computed(() => {
		const coins = this.user()?.coins;
		return coins !== undefined && coins >= this.totalPrice();
	});

	ngOnInit(): void {
		this.titleService.setTitle('Team Peps - ' + this.translate.instant('page.shop.title'));
		window.scrollTo({ top: 0, behavior: 'smooth' });
		if (this.authService.isAuthenticated()) {
			this.refreshUser();
		}
		this.loadBoostersShop();
	}

	/** loadUser() rafraîchit le signal partagé de l'AuthService (solde PepsCoins) en plus du signal local. */
	private refreshUser(): void {
		this.authService.loadUser().subscribe({
			next: (user) => this.user.set(user),
			error: () => {},
		});
	}

	isAuthenticated(): boolean {
		return this.authService.isAuthenticated();
	}

	isDisabled(booster: Booster): boolean {
		const coins = this.user()?.coins;
		return coins !== undefined && coins < booster.price;
	}

	buttonLabel(booster: Booster): string {
		if (!this.isAuthenticated()) return this.translate.instant('page.shop.login-required');
		if (this.isDisabled(booster)) return this.translate.instant('page.shop.insufficient');
		return this.translate.instant('page.shop.buy');
	}

	openModal(booster: Booster): void {
		if (!this.isAuthenticated() || this.isDisabled(booster)) return;
		this.quantity.set(1);
		this.selectedBooster.set(booster);
	}

	setQuantity(value: number): void {
		const clamped = Math.max(1, Math.min(this.maxAffordable(), Math.floor(value) || 1));
		this.quantity.set(clamped);
	}

	incrementQuantity(): void {
		this.setQuantity(this.quantity() + 1);
	}

	decrementQuantity(): void {
		this.setQuantity(this.quantity() - 1);
	}

	onQuantityInput(event: Event): void {
		this.setQuantity(Number((event.target as HTMLInputElement).value));
	}

	closeModal(): void {
		this.selectedBooster.set(null);
	}

	onOverlayClick(event: Event): void {
		if (event.target === event.currentTarget) this.closeModal();
	}

	confirmPurchase(): void {
		const booster = this.selectedBooster();
		if (!booster || this.isPurchasing() || !this.canAfford()) return;
		this.isPurchasing.set(true);
		this.lastOpenedBoosterId = booster.id;
		this.boosterService.purchaseBooster(booster.id, this.quantity()).subscribe({
			next: (cards) => {
				// Le back a ouvert 1 booster et déposé les autres dans l'inventaire.
				this.boosterStateService.setCards(cards);
				this.closeModal();
				this.isPurchasing.set(false);
				this.boosterStateService.open();
				this.refreshUser();
				this.loadInventory();
			},
			error: (err) => {
				console.error('Purchase failed:', err);
				this.isPurchasing.set(false);
				this.toastService.show(this.translate.instant('page.shop.purchase.error'), 'error', 5000);
			}
		});
	}

	/** Depuis l'écran d'ouverture : enchaîne sur le même type de booster s'il en reste, sinon sur le prochain type disponible. */
	openNextBooster(): void {
		if (this.isOpeningNext()) return;
		const boosters = this.inventory()?.boosters ?? [];
		const next = boosters.find(b => b.booster.id === this.lastOpenedBoosterId && b.quantity > 0)
			?? boosters.find(b => b.quantity > 0);
		if (!next) return;
		this.lastOpenedBoosterId = next.booster.id;
		this.isOpeningNext.set(true);
		this.boosterService.openBooster(next.booster.id).subscribe({
			next: (cards) => {
				this.boosterStateService.setCards(cards);
				this.boosterStateService.open();
				this.isOpeningNext.set(false);
				this.loadInventory();
			},
			error: () => {
				this.isOpeningNext.set(false);
				this.toastService.show(this.translate.instant('page.shop.purchase.error'), 'error', 5000);
			}
		});
	}

	private loadInventory(): void {
		if (!this.isAuthenticated()) return;
		this.inventoryService.getInventory().subscribe({
			next: (data) => this.inventory.set(data),
			error: () => {},
		});
	}

	private loadBoostersShop() {
		this.boosterService.getBoosters().subscribe({
			next: (boosters) => this.boosters.set(boosters),
			error: () => {},
		});
	}
}
